import React, { useState, useEffect } from "react";
import API_URL from "./config";
import HelpTooltip from "./HelpTooltip";
import "./LineupModal.css";

const avg = (arr) => arr.reduce((a, b) => a + b, 0) / arr.length;
const playerOverall = (p) => {
  const offense = avg([p.close, p.mid, p.three, p.free_throw, p.dunk, p.passing, p.dribbling, p.offensive_iq]);
  const defense = avg([p.defensive_iq, p.rebounding, p.strength, p.jumping]);
  const athletic = avg([p.speed, p.stamina, p.injury, p.jumping]);
  return Math.round(offense * 0.45 + defense * 0.35 + athletic * 0.2);
};

const DEFAULT_STARTER_MINUTES = 34;
const DEFAULT_BENCH_MINUTES = 14;

export default function LineupModal({ roster, teamId, onClose }) {
  const [onField, setOnField] = useState([]);
  const [bench, setBench] = useState([]);
  const [available, setAvailable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [hasExistingLineup, setHasExistingLineup] = useState(false);
  const [minutesByPlayerId, setMinutesByPlayerId] = useState({});
  const [draggedPlayerId, setDraggedPlayerId] = useState(null);

  // On mount, load existing lineup if available
  useEffect(() => {
    const loadLineup = async () => {
      setLoading(true);
      try {
        const activeSaveId = localStorage.getItem("activeSaveId");
        const user = JSON.parse(localStorage.getItem("user") || "{}");

        const res = await fetch(`${API_URL}/api/teams/${teamId}?save_id=${activeSaveId}`, {
          headers: { "X-User-ID": user._id },
        });

        if (!res.ok) {
          // Team endpoint might not return lineup, load empty instead
          setAvailable(sortPlayers(roster));
          setHasExistingLineup(false);
          setLoading(false);
          return;
        }

        const data = await res.json();
        const team = data.team || {};
        const lineupData = team.current_lineup;

        if (lineupData && lineupData.on_field && lineupData.bench) {
          // Map IDs back to full player objects
          const onFieldPlayers = lineupData.on_field
            .map(id => roster.find(p => p._id === id))
            .filter(p => p);
          const benchPlayers = lineupData.bench
            .map(id => roster.find(p => p._id === id))
            .filter(p => p);
          
          const usedIds = new Set([...lineupData.on_field, ...lineupData.bench]);
          const availablePlayers = roster.filter(p => !usedIds.has(p._id));

          // Initialize minutes from existing data or use defaults
          const minutes = {};
          onFieldPlayers.forEach(p => {
            minutes[p._id] = lineupData.minutesByPlayerId?.[p._id] ?? DEFAULT_STARTER_MINUTES;
          });
          benchPlayers.forEach(p => {
            minutes[p._id] = lineupData.minutesByPlayerId?.[p._id] ?? DEFAULT_BENCH_MINUTES;
          });

          setOnField(onFieldPlayers);
          setBench(benchPlayers);
          setAvailable(sortPlayers(availablePlayers));
          setMinutesByPlayerId(minutes);
          setHasExistingLineup(true);
        } else {
          // No existing lineup
          setAvailable(sortPlayers(roster));
          setHasExistingLineup(false);
        }
      } catch (e) {
        // Failed to load existing lineup
        setAvailable(sortPlayers(roster));
        setHasExistingLineup(false);
      } finally {
        setLoading(false);
      }
    };

    loadLineup();
  }, [roster, teamId]);

  const sortPlayers = (players) => {
    return [...players].sort((a, b) => {
      const nameA = (a.displayName || a.name || "").toLowerCase();
      const nameB = (b.displayName || b.name || "").toLowerCase();
      return nameA.localeCompare(nameB);
    });
  };

  const sortByOVR = () => {
    const sorted = [...available].sort((a, b) => playerOverall(b) - playerOverall(a));
    setAvailable(sorted);
  };

  // Drag and drop handlers
  const handleDragStart = (e, player, source, sourceIndex) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/json", JSON.stringify({ player, source, sourceIndex }));
    
    // Set dragged player for visual feedback
    setDraggedPlayerId(player._id);
    
    // Create custom drag image to show the player card
    const dragImage = document.createElement("div");
    dragImage.style.position = "absolute";
    dragImage.style.left = "-9999px";
    dragImage.style.background = "rgba(18, 82, 210, 0.9)";
    dragImage.style.border = "2px solid rgba(76, 175, 80, 0.6)";
    dragImage.style.borderRadius = "8px";
    dragImage.style.padding = "12px 16px";
    dragImage.style.color = "white";
    dragImage.style.fontWeight = "600";
    dragImage.style.whiteSpace = "nowrap";
    dragImage.style.boxShadow = "0 8px 24px rgba(0, 0, 0, 0.4)";
    dragImage.textContent = `${player.displayName || player.name} (${player.position})`;
    document.body.appendChild(dragImage);
    
    e.dataTransfer.setDragImage(dragImage, 20, 20);
    
    // Clean up after a short delay
    setTimeout(() => document.body.removeChild(dragImage), 0);
  };

  const handleDragEnd = (e) => {
    setDraggedPlayerId(null);
  };

  const clearDragState = () => {
    setDraggedPlayerId(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDropOnField = (e, index) => {
    e.preventDefault();
    const data = JSON.parse(e.dataTransfer.getData("application/json"));
    const { player, source, sourceIndex } = data;

    const newOnField = [...onField];
    const targetPlayer = newOnField[index]; // Player at target spot (if any)

    if (source === "onField") {
      // Swapping within on-field section
      if (!targetPlayer || sourceIndex === index) {
        clearDragState();
        return; // No swap needed
      }
      newOnField[index] = player;
      newOnField[sourceIndex] = targetPlayer;
      setOnField(newOnField);
      clearDragState();
      return;
    }

    // Place new player on field
    newOnField[index] = player;
    setOnField(newOnField);

    // Handle source updates - remove dragged player from source
    if (source === "available") {
      const filteredAvailable = available.filter(p => p._id !== player._id);
      // If swapping, add target player to available; otherwise just filter
      if (targetPlayer) {
        setAvailable([...filteredAvailable, targetPlayer].sort((a, b) => {
          const nameA = (a.displayName || a.name || "").toLowerCase();
          const nameB = (b.displayName || b.name || "").toLowerCase();
          return nameA.localeCompare(nameB);
        }));
        // Update minutes: dragged player goes to starters
        setMinutesByPlayerId((prev) => ({
          ...prev,
          [player._id]: DEFAULT_STARTER_MINUTES,
          [targetPlayer._id]: undefined,
        }));
      } else {
        setAvailable(filteredAvailable);
        setMinutesByPlayerId((prev) => ({
          ...prev,
          [player._id]: DEFAULT_STARTER_MINUTES,
        }));
      }
    } else if (source === "bench") {
      const filteredBench = bench.filter(p => p._id !== player._id);
      if (targetPlayer) {
        setBench([...filteredBench, targetPlayer]);
        // Update minutes: dragged player goes to starters, target goes to bench
        setMinutesByPlayerId((prev) => ({
          ...prev,
          [player._id]: DEFAULT_STARTER_MINUTES,
          [targetPlayer._id]: DEFAULT_BENCH_MINUTES,
        }));
      } else {
        setBench(filteredBench);
        setMinutesByPlayerId((prev) => ({
          ...prev,
          [player._id]: DEFAULT_STARTER_MINUTES,
        }));
      }
    }
    clearDragState();
  };

  const handleDropBench = (e, index) => {
    e.preventDefault();
    const data = JSON.parse(e.dataTransfer.getData("application/json"));
    const { player, source, sourceIndex } = data;

    const newBench = [...bench];
    const targetPlayer = newBench[index]; // Player at target spot (if any)

    if (source === "bench") {
      // Swapping within bench section
      if (!targetPlayer || sourceIndex === index) {
        clearDragState();
        return; // No swap needed
      }
      newBench[index] = player;
      newBench[sourceIndex] = targetPlayer;
      setBench(newBench);
      clearDragState();
      return;
    }

    // Place new player on bench
    newBench[index] = player;
    setBench(newBench);

    // Handle source updates - remove dragged player from source
    if (source === "available") {
      const filteredAvailable = available.filter(p => p._id !== player._id);
      if (targetPlayer) {
        setAvailable([...filteredAvailable, targetPlayer].sort((a, b) => {
          const nameA = (a.displayName || a.name || "").toLowerCase();
          const nameB = (b.displayName || b.name || "").toLowerCase();
          return nameA.localeCompare(nameB);
        }));
        // Update minutes: dragged player goes to bench, target goes to available
        setMinutesByPlayerId((prev) => {
          const next = { ...prev };
          next[player._id] = DEFAULT_BENCH_MINUTES;
          delete next[targetPlayer._id];
          return next;
        });
      } else {
        setAvailable(filteredAvailable);
        setMinutesByPlayerId((prev) => ({
          ...prev,
          [player._id]: DEFAULT_BENCH_MINUTES,
        }));
      }
    } else if (source === "onField") {
      const filteredOnField = onField.filter(p => p._id !== player._id);
      if (targetPlayer) {
        setOnField([...filteredOnField, targetPlayer]);
        // Update minutes: dragged player goes to bench, target goes to on-field
        setMinutesByPlayerId((prev) => ({
          ...prev,
          [player._id]: DEFAULT_BENCH_MINUTES,
          [targetPlayer._id]: DEFAULT_STARTER_MINUTES,
        }));
      } else {
        setOnField(filteredOnField);
        setMinutesByPlayerId((prev) => ({
          ...prev,
          [player._id]: DEFAULT_BENCH_MINUTES,
        }));
      }
    }
    clearDragState();
  };

  const handleDropAvailable = (e, targetIndex) => {
    e.preventDefault();
    const data = JSON.parse(e.dataTransfer.getData("application/json"));
    const { player, source, sourceIndex } = data;

    if (source === "available") {
      // Swapping within available section
      if (targetIndex === sourceIndex) {
        clearDragState();
        return; // No swap needed
      }
      const newAvailable = [...available];
      const targetPlayer = newAvailable[targetIndex];
      newAvailable[targetIndex] = player;
      newAvailable[sourceIndex] = targetPlayer;
      setAvailable(newAvailable);
      clearDragState();
      return;
    }

    // Remove from source and add to available
    if (source === "onField") {
      const filteredOnField = onField.filter(p => p._id !== player._id);
      setOnField(filteredOnField);
      setMinutesByPlayerId((prev) => {
        const next = { ...prev };
        delete next[player._id];
        return next;
      });
    } else if (source === "bench") {
      const filteredBench = bench.filter(p => p._id !== player._id);
      setBench(filteredBench);
      setMinutesByPlayerId((prev) => {
        const next = { ...prev };
        delete next[player._id];
        return next;
      });
    }

    setAvailable(sortPlayers([...available, player]));
    clearDragState();
  };

  const addToOnField = (player) => {
    const filledCount = onField.filter(p => p).length;
    if (filledCount < 5) {
      // Find the first empty slot
      const newOnField = [...onField];
      const emptyIndex = newOnField.findIndex(p => !p);
      if (emptyIndex !== -1) {
        newOnField[emptyIndex] = player;
      } else {
        // If no empty slots exist in array, just append
        newOnField.push(player);
      }
      setOnField(newOnField);
      setAvailable(available.filter(p => p._id !== player._id));
      setMinutesByPlayerId((prev) => ({
        ...prev,
        [player._id]: prev[player._id] ?? DEFAULT_STARTER_MINUTES,
      }));
    }
  };

  const addToBench = (player) => {
    const filledCount = bench.filter(p => p).length;
    if (filledCount < 5) {
      // Find the first empty slot
      const newBench = [...bench];
      const emptyIndex = newBench.findIndex(p => !p);
      if (emptyIndex !== -1) {
        newBench[emptyIndex] = player;
      } else {
        // If no empty slots exist in array, just append
        newBench.push(player);
      }
      setBench(newBench);
      setAvailable(available.filter(p => p._id !== player._id));
      setMinutesByPlayerId((prev) => ({
        ...prev,
        [player._id]: prev[player._id] ?? DEFAULT_BENCH_MINUTES,
      }));
    }
  };

  const removeFromOnField = (playerId) => {
    const player = onField.find(p => p._id === playerId);
    setOnField(onField.filter(p => p._id !== playerId));
    setAvailable([...available, player].sort((a, b) => {
      const nameA = (a.displayName || a.name || "").toLowerCase();
      const nameB = (b.displayName || b.name || "").toLowerCase();
      return nameA.localeCompare(nameB);
    }));
    setMinutesByPlayerId((prev) => {
      const next = { ...prev };
      delete next[playerId];
      return next;
    });
  };

  const removeFromBench = (playerId) => {
    const player = bench.find(p => p._id === playerId);
    setBench(bench.filter(p => p._id !== playerId));
    setAvailable([...available, player].sort((a, b) => {
      const nameA = (a.displayName || a.name || "").toLowerCase();
      const nameB = (b.displayName || b.name || "").toLowerCase();
      return nameA.localeCompare(nameB);
    }));
    setMinutesByPlayerId((prev) => {
      const next = { ...prev };
      delete next[playerId];
      return next;
    });
  };

  const getMinutesForPlayer = (playerId) => minutesByPlayerId[playerId] ?? 0;

  const getTotalMinutes = () => {
    return Object.values(minutesByPlayerId).reduce((sum, min) => sum + (Number(min) || 0), 0);
  };

  const isOnFieldPlayer = (playerId) => {
    return onField.some(p => p._id === playerId);
  };

  // Check if there are empty slots (undefined values) in onField
  const hasEmptyOnFieldSlots = () => {
    return onField.filter(p => p).length < 5;
  };

  // Check if there are empty slots (undefined values) in bench
  const hasEmptyBenchSlots = () => {
    return bench.filter(p => p).length < 5;
  };

  const setMinutesForPlayer = (playerId, rawValue) => {
    // Allow any value (including empty string) while typing
    setMinutesByPlayerId((prev) => ({
      ...prev,
      [playerId]: rawValue === "" ? "" : rawValue,
    }));
  };

  const validateAndClampMinutes = (playerId) => {
    const rawValue = minutesByPlayerId[playerId] ?? 0;
    const parsed = Number(rawValue);
    
    // If empty or NaN, default to 0
    if (rawValue === "" || Number.isNaN(parsed)) {
      setMinutesByPlayerId((prev) => ({
        ...prev,
        [playerId]: 0,
      }));
      return;
    }
    
    // Clamp to 0-48
    const clamped = Math.max(0, Math.min(48, Math.floor(parsed)));
    setMinutesByPlayerId((prev) => ({
      ...prev,
      [playerId]: clamped,
    }));
  };

  const saveLineup = async () => {
    const filledOnField = onField.filter(p => p);
    const filledBench = bench.filter(p => p);

    if (filledOnField.length !== 5 || filledBench.length !== 5) {
      setError("You must select exactly 5 on-field and 5 bench players");
      return;
    }

    const totalMinutes = getTotalMinutes();
    if (totalMinutes !== 240) {
      setError(`Total minutes must equal 240 (you have ${totalMinutes})`);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const activeSaveId = localStorage.getItem("activeSaveId");
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const res = await fetch(`${API_URL}/api/teams/${teamId}/lineup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-User-ID": user._id,
        },
        body: JSON.stringify({
          save_id: activeSaveId,
          on_field: filledOnField.map(p => p._id),
          bench: filledBench.map(p => p._id),
          minutesByPlayerId: minutesByPlayerId,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || "Failed to save lineup");
        return;
      }

      onClose();
    } catch (e) {
      setError("Server error when saving lineup");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lineup-overlay" onClick={onClose}>
      <div className="lineup-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="lineup-header">
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <h2 className="lineup-title">{hasExistingLineup ? "Edit Lineup" : "Create Lineup"}</h2>
            <HelpTooltip 
              text="Drag and drop players between On Field and Bench. Adjust playing time for each player." 
              position="right"
            />
          </div>
          <button className="lineup-close-btn" onClick={onClose}>✕</button>
        </div>

        {error && <div className="lineup-error">{error}</div>}

        {loading ? (
          <div className="lineup-body" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "300px" }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>⏳</div>
              <p style={{ color: "#9ca3af" }}>Loading your lineup...</p>
            </div>
          </div>
        ) : (
        <div className="lineup-body">
          <div className="lineup-columns">
            {/* Left: On-Field Players */}
            <div className="lineup-column">
              <h3 className="column-title">On Field ({onField.filter(p => p).length}/5)</h3>
              <div className="lineup-slots">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={`on-field-${i}`}
                    className="lineup-slot on-field-slot"
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDropOnField(e, i)}
                    style={{ cursor: "drop" }}
                  >
                    {onField[i] ? (
                      <div
                        className="lineup-player-card"
                        draggable
                        onDragStart={(e) => handleDragStart(e, onField[i], "onField", i)}
                        onDragEnd={handleDragEnd}
                        style={{ 
                          cursor: "grab",
                          opacity: draggedPlayerId === onField[i]._id ? 0.5 : 1,
                          transition: "opacity 0.2s ease",
                          position: "relative"
                        }}
                      >
                        {onField[i].injuryType && onField[i].injuryGamesRemaining > 0 && (
                          <div 
                            title="This player is injured. It is advised to let them recover before putting them in a game."
                            style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: "24px",
                            height: "24px",
                            border: "2px dashed #f87171",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            fontSize: "12px",
                            color: "#f87171",
                            zIndex: 10,
                            pointerEvents: "auto",
                            cursor: "help"
                          }}>
                            !
                          </div>
                        )}
                        <div className="player-info">
                          <span className="player-name">{onField[i].displayName || onField[i].name}</span>
                          <span className="player-pos">{onField[i].position}</span>
                          <span className="player-ovr">OVR {playerOverall(onField[i])}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.5rem" }}>
                          <label className="lineup-minute-label">Min:</label>
                          <input
                            type="number"
                            min="0"
                            max="48"
                            className="lineup-minute-input"
                            value={getMinutesForPlayer(onField[i]._id)}
                            onChange={(e) => setMinutesForPlayer(onField[i]._id, e.target.value)}
                            onBlur={() => validateAndClampMinutes(onField[i]._id)}
                          />
                        </div>
                        <button
                          className="remove-btn"
                          onClick={() => removeFromOnField(onField[i]._id)}
                          title="Remove player"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div className="empty-slot">Slot {i + 1}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Middle: Available Players */}
            <div className="lineup-column available-column">
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <h3 className="column-title">Available ({available.length}/15)</h3>
                <button className="sort-btn" onClick={sortByOVR} title="Sort by Overall">
                  ⬇
                </button>
              </div>
              <div
                className="available-list"
                onDragOver={handleDragOver}
              >
                {available.map((player, idx) => (
                  <div
                    key={player._id}
                    className="available-player"
                    draggable
                    onDragStart={(e) => handleDragStart(e, player, "available", idx)}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDropAvailable(e, idx)}
                    style={{ 
                      cursor: "grab",
                      opacity: draggedPlayerId === player._id ? 0.5 : 1,
                      transition: "opacity 0.2s ease",
                      position: "relative"
                    }}
                  >
                    {player.injuryType && player.injuryGamesRemaining > 0 && (
                      <div 
                        title="This player is injured. It is advised to let them recover before putting them in a game."
                        style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "28px",
                        height: "28px",
                        border: "2px dashed #f87171",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "bold",
                        fontSize: "14px",
                        color: "#f87171",
                        zIndex: 10,
                        pointerEvents: "auto",
                        cursor: "help"
                      }}>
                        !
                      </div>
                    )}
                    <div className="player-info">
                      <span className="player-name">{player.displayName || player.name}</span>
                      <span className="player-pos">{player.position}</span>
                      <span className="player-ovr">OVR {playerOverall(player)}</span>
                    </div>
                    <div className="action-buttons">
                      <button
                        className="add-btn"
                        onClick={() => addToOnField(player)}
                        disabled={!hasEmptyOnFieldSlots()}
                        title="Add to on-field"
                      >
                        Start
                      </button>
                      <button
                        className="add-btn bench-add"
                        onClick={() => addToBench(player)}
                        disabled={!hasEmptyBenchSlots()}
                        title="Add to bench"
                      >
                        Bench
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Bench Players */}
            <div className="lineup-column">
              <h3 className="column-title">Bench ({bench.filter(p => p).length}/5)</h3>
              <div className="lineup-slots">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={`bench-${i}`}
                    className="lineup-slot bench-slot"
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDropBench(e, i)}
                    style={{ cursor: "drop" }}
                  >
                    {bench[i] ? (
                      <div
                        className="lineup-player-card"
                        draggable
                        onDragStart={(e) => handleDragStart(e, bench[i], "bench", i)}
                        onDragEnd={handleDragEnd}
                        style={{ 
                          cursor: "grab",
                          opacity: draggedPlayerId === bench[i]._id ? 0.5 : 1,
                          transition: "opacity 0.2s ease",
                          position: "relative"
                        }}
                      >
                        {bench[i].injuryType && bench[i].injuryGamesRemaining > 0 && (
                          <div 
                            title="This player is injured. It is advised to let them recover before putting them in a game."
                            style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: "28px",
                            height: "28px",
                            border: "2px dashed #f87171",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            fontSize: "14px",
                            color: "#f87171",
                            zIndex: 10,
                            pointerEvents: "auto",
                            cursor: "help"
                          }}>
                            !
                          </div>
                        )}
                        <div className="player-info">
                          <span className="player-name">{bench[i].displayName || bench[i].name}</span>
                          <span className="player-pos">{bench[i].position}</span>
                          <span className="player-ovr">OVR {playerOverall(bench[i])}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.5rem" }}>
                          <label className="lineup-minute-label">Min:</label>
                          <input
                            type="number"
                            min="0"
                            max="48"
                            className="lineup-minute-input"
                            value={getMinutesForPlayer(bench[i]._id)}
                            onChange={(e) => setMinutesForPlayer(bench[i]._id, e.target.value)}
                            onBlur={() => validateAndClampMinutes(bench[i]._id)}
                          />
                        </div>
                        <button
                          className="remove-btn"
                          onClick={() => removeFromBench(bench[i]._id)}
                          title="Remove player"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div className="empty-slot">Slot {i + 1}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        )}

        {!loading && (
          <div className="lineup-footer" style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            {/* Minutes Display */}
            <div style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: "0.5rem",
              minWidth: "180px"
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: "0.8rem", color: "rgba(255, 255, 255, 0.6)" }}>
                  Minutes
                </span>
                <span style={{ fontSize: "0.9rem", fontWeight: "600", color: getTotalMinutes() > 240 ? "#ef4444" : "#22c55e" }}>
                  {getTotalMinutes()} / 240
                </span>
              </div>
              <div style={{
                width: "100%",
                height: "8px",
                background: "rgba(255, 255, 255, 0.1)",
                borderRadius: "4px",
                overflow: "hidden",
                border: `1px solid ${getTotalMinutes() > 240 ? "#ef4444" : "#3b82f6"}`
              }}>
                <div style={{
                  height: "100%",
                  width: `${Math.min((getTotalMinutes() / 240) * 100, 100)}%`,
                  background: getTotalMinutes() > 240 ? "#ef4444" : "#3b82f6",
                  transition: "width 0.2s ease"
                }} />
              </div>
            </div>

            {/* Buttons */}
            <button className="lineup-cancel-btn" onClick={onClose}>Cancel</button>
            <button
              className="lineup-save-btn"
              onClick={saveLineup}
              disabled={loading || onField.length !== 5 || bench.length !== 5 || getTotalMinutes() !== 240}
            >
              {loading ? "Saving..." : "Save Lineup"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
